import * as React from 'react';
import { useSelector } from 'react-redux';
import {
  PaginatorAndListSettings,
  PageTitle,
  Search,
  Section,
  selectUser,
  useCacheQuery,
  useCloneEntity,
  getConfigOrThrow,
  GeneralProps,
  addControlColumn,
} from 'gossamer-front-end';
import { EventHostView } from 'cb-model';
import { getFilter, QueryOptions } from 'gossamer-unified';
import { DownloadButton } from 'packages/shared-front-end';

export const HostEventSearch = (): React.JSX.Element => {
  const user = useSelector(selectUser);
  const now = React.useRef(new Date().toISOString());
  const hostCondition = React.useRef(getFilter('user_id', 'eq', user.userId, 'hidden'));
  const [queryOptionsFuture, setQueryOptionsFuture] = React.useState({
    where: {
      and: [
        getFilter('title', 'sw', ''),
        hostCondition.current,
        getFilter('visibility_limit', 'ne', 'cancelled', 'hidden'),
        getFilter('type', 'ne', 'on-demand', 'hidden'),
        getFilter('start_at', 'gt', now.current, 'hidden'),
      ],
    },
    order: [['start_at', 'ASC']],
  } as QueryOptions);

  const [queryOptionsPast, setQueryOptionsPast] = React.useState({
    where: {
      and: [
        getFilter('title', 'sw', ''),
        hostCondition.current,
        getFilter('visibility_limit', 'ne', 'cancelled', 'hidden'),
        getFilter('type', 'ne', 'on-demand', 'hidden'),
        getFilter('start_at', 'lt', now.current, 'hidden'),
      ],
    },
    order: [['start_at', 'DESC']],
  } as QueryOptions);

  const [queryOptionsOnDemand, setQueryOptionsOnDemand] = React.useState({
    where: {
      and: [
        getFilter('title', 'sw', ''),
        hostCondition.current,
        getFilter('visibility_limit', 'ne', 'cancelled', 'hidden'),
        getFilter('type', 'eq', 'on-demand', 'hidden'),
      ],
    },
    order: [['start_at', 'DESC']],
  } as QueryOptions);

  const [queryOptionsCancelled, setQueryOptionsCancelled] = React.useState({
    where: {
      and: [
        getFilter('title', 'sw', ''),
        hostCondition.current,
        getFilter('visibility_limit', 'eq', 'cancelled', 'hidden'),
      ],
    },
    order: [['start_at', 'DESC']],
  } as QueryOptions);

  const LocalPastEventHostView = useCloneEntity(EventHostView, (local) => {
    addControlColumn(
      local,
      'host_certificate_id',
      999,
      (row) =>
        [getConfigOrThrow('gossamer-be-origin'), 'gossamer/main/certificate-host', row.event_id, row.user_id].join('/'),
      (props) => <DownloadButton url={props.value}>certificate</DownloadButton>,
    );
  });

  hostCondition.current.value = user.userId;

  const gCacheQueryItemFuture = useCacheQuery(EventHostView, queryOptionsFuture, !user.userId);
  const gCacheQueryItemPast = useCacheQuery(EventHostView, queryOptionsPast, !user.userId);
  const gCacheQueryItemOnDemand = useCacheQuery(EventHostView, queryOptionsOnDemand, !user.userId);
  const gCacheQueryItemCancelled = useCacheQuery(EventHostView, queryOptionsCancelled, !user.userId);

  return (
    <>
      <PageTitle title="My Hosted Events" />
      <Section title="Upcoming Events I am Hosting" subtitle="Future Events of which I am the Main Host or Co-Host">
        <PaginatorAndListSettings
          entity={EventHostView}
          gCacheQuery={gCacheQueryItemFuture}
          queryOptions={queryOptionsFuture}
          render={Search}
          setQueryOptions={setQueryOptionsFuture}
        />
      </Section>

      <Section title="Past Events I Hosted" subtitle="Past Events of which I was the Main Host or Co-Host">
        <PaginatorAndListSettings
          entity={LocalPastEventHostView}
          gCacheQuery={gCacheQueryItemPast}
          queryOptions={queryOptionsPast}
          render={Search}
          setQueryOptions={setQueryOptionsPast}
        />
      </Section>

      <Section title="On-Demand Events I am Hosting" subtitle="On-Demand Events of which I am the Main Host or Co-Host">
        <PaginatorAndListSettings
          entity={EventHostView}
          gCacheQuery={gCacheQueryItemOnDemand}
          queryOptions={queryOptionsOnDemand}
          render={Search}
          setQueryOptions={setQueryOptionsOnDemand}
        />
      </Section>

      <Section
        title="Cancelled Events I was Hosting"
        subtitle="Cancelled Events of which I was the Main Host or Co-Host"
      >
        <PaginatorAndListSettings
          entity={EventHostView}
          gCacheQuery={gCacheQueryItemCancelled}
          queryOptions={queryOptionsCancelled}
          render={Search}
          setQueryOptions={setQueryOptionsCancelled}
        />
      </Section>
    </>
  );
};
